import { createRootRoute, Link, Outlet, useLocation, useNavigate, useRouter, HeadContent } from '@tanstack/react-router'
import { Fragment, Suspense, lazy, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

const TanStackRouterDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-router-devtools').then((res) => ({
        default: res.TanStackRouterDevtools,
      })),
    )
  : () => null

const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((res) => ({
        default: res.ReactQueryDevtools,
      })),
    )
  : () => null
import type { ButtonHTMLAttributes, KeyboardEvent, ReactNode, RefObject } from 'react'
import { GAMES, GITHUB_HANDLE, GITHUB_URL } from '../content'
import { ROUTES, dirFor, isDestination, pathFor, run, tab, type Completing, type Row, type To } from '../terminal'

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'jfrader' },
      /* One card for every route: the penguin, drawn from the same art the site
         renders, with the amber grid, glow and a waiting cursor. No words. */
      { property: 'og:image', content: 'https://jfrader.com/og.png' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:image:alt', content: 'The jfrader penguin' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:image', content: 'https://jfrader.com/og.png' },
    ],
  }),
  component: RootComponent,
})

type Line = { id: number; node: ReactNode } | { id: number; row: Row }

/* Shell output is stored as its row, not as rendered markup, so it is drawn
   with the callback of the current render: a row printed before a navigation
   still runs in the directory the visitor is in when they click it. Echoes and
   narration prompts are stored as nodes, because they are history. */
const holdsRow = (line: Line): line is { id: number; row: Row } => 'row' in line

const Prompt = ({ cmd, cwd }: { cmd: string; cwd: string }) => (
  <>
    <span className="ps1">
      user@jfrader <i>{cwd}</i> ❯
    </span>{' '}
    {cmd}
  </>
)

/* The terminal's links are route-qualified on purpose. Its log survives
   navigation, so a bare `#sec-work` rendered on / is still clickable once you
   are on /contact, where it would resolve to /contact#sec-work and find
   nothing. Naming the route makes the link correct wherever it is clicked. */
const Jump = ({ to, hash, children }: { to: To; hash?: string; children: ReactNode }) => (
  <Link className="jump" to={to} hash={hash}>
    {children}
  </Link>
)

function DestinationButton({ to, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { to: To }) {
  const router = useRouter()
  const preload = () => {
    void router.preloadRoute({ to }).catch(() => {})
  }
  return <button type="button" {...props} onMouseEnter={preload} onFocus={preload} onTouchStart={preload} />
}

const XLink = ({ href, children }: { href: string; children: ReactNode }) => (
  <a className="xlink" href={href} target="_blank" rel="noreferrer">
    {children}
  </a>
)

const Indent = () => <>{'  '}</>

/** One row of shell output. Links stay links, and a `run` row runs that command. */
function RowView({ row, onRun }: { row: Row; onRun: (command: string) => void }) {
  if (row.kind === 'gap') return <span className="muted">{'\u00a0'}</span>
  if (row.kind === 'error') return <span className="err">{row.text}</span>
  if (row.kind === 'text') return <span className={row.muted ? 'muted' : undefined}>{row.text}</span>

  const { name, note, to, hash, href, run: command } = row
  return (
    <>
      <Indent />
      {command ? (
        <button type="button" className="jump jump--run" onClick={() => onRun(command)}>
          {name}
        </button>
      ) : to ? (
        <Jump to={to} hash={hash}>
          {name}
        </Jump>
      ) : href ? (
        <XLink href={href}>{name}</XLink>
      ) : (
        <span>{name}</span>
      )}
      {note ? (
        <>
          {' '}
          <span className="muted">{note}</span>
        </>
      ) : null}
    </>
  )
}

/** Types a command one character at a time, then hands over once. */
function useTypewriter(text: string, onDone: () => void, charMs = 26, runKey = '', still = false) {
  const [typed, setTyped] = useState<string | null>(null)
  const done = useRef(onDone)
  done.current = onDone

  /* runKey re-runs the typing for the same text: landing on another route types
     the same `cat README.md` again, and the text alone would not restart it. */
  useEffect(() => {
    /* No typing when the visitor asked for less motion: the line appears, then
       its output does. */
    if (still) {
      setTyped(null)
      done.current()
      return
    }
    let i = 0
    let timer = 0
    let cancelled = false
    setTyped('')
    const tick = () => {
      if (cancelled) return
      i += 1
      setTyped(text.slice(0, i))
      if (i < text.length) {
        timer = window.setTimeout(tick, charMs + Math.random() * 18)
      } else {
        timer = window.setTimeout(() => {
          if (cancelled) return
          setTyped(null)
          done.current()
        }, 160)
      }
    }
    tick()
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [text, charMs, runKey, still])

  return typed
}

/* A terminal line: a transparent <input> layered over rendered text so typing,
   arrows, Home/End, Delete and backspace are native, and the block cursor sits
   at the caret. The input stays in the accessibility tree as a text field. */
function TerminalInput({
  onSubmit,
  placeholder,
  fieldRef,
  className = 'init__live',
  history,
  onRun,
  onTab,
  onEscape,
  autoFocus,
}: {
  onSubmit: (raw: string) => void
  placeholder: string
  fieldRef?: RefObject<HTMLInputElement | null>
  className?: string
  /* the shell owns the history so the intro's field and the rail's field share
     one list across the transition */
  history: string[]
  onRun: (raw: string) => void
  /** Tab: the shell answers with the candidate list and the line it makes. */
  onTab: (value: string, previous: Completing | null) => Completing & { value: string }
  /** Escape: where the keyboard goes when it leaves the terminal. */
  onEscape?: () => void
  autoFocus?: boolean
}) {
  const [value, setValue] = useState('')
  const [sel, setSel] = useState(0)
  const [focused, setFocused] = useState(false)
  const [histIdx, setHistIdx] = useState(-1)
  /* the completion in progress, so the next Tab walks the same candidates
     instead of starting over from the value it just wrote */
  const [cycle, setCycle] = useState<Completing | null>(null)
  const localRef = useRef<HTMLInputElement | null>(null)
  const inputRef = fieldRef ?? localRef

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus({ preventScroll: true })
    }
  }, [autoFocus])

  const syncSel = useCallback(() => {
    const el = inputRef.current
    if (el) setSel(el.selectionStart ?? el.value.length)
  }, [])

  const setValueEnd = useCallback((next: string) => {
    setValue(next)
    setSel(next.length)
    requestAnimationFrame(() => {
      const el = inputRef.current
      if (el) {
        el.setSelectionRange(next.length, next.length)
        el.focus()
      }
    })
  }, [])

  const commit = useCallback(() => {
    const raw = value.trim()
    if (raw) onRun(raw)
    setValue('')
    setSel(0)
    setHistIdx(-1)
    setCycle(null)
    onSubmit(raw)
  }, [value, onSubmit, onRun])

  const recall = useCallback(
    (dir: -1 | 1) => {
      if (history.length === 0) return
      setCycle(null)
      if (dir === -1) {
        // Up: most recent first
        const next = histIdx === -1 ? history.length - 1 : Math.max(0, histIdx - 1)
        setHistIdx(next)
        setValueEnd(history[next])
      } else {
        // Down: forward; past the newest recalls an empty line
        if (histIdx === -1) return
        const next = histIdx + 1
        if (next >= history.length) {
          setHistIdx(-1)
          setValueEnd('')
        } else {
          setHistIdx(next)
          setValueEnd(history[next])
        }
      }
    },
    [history, histIdx, setValueEnd],
  )

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commit()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      recall(-1)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      recall(1)
    } else if (e.key === 'Escape') {
      /* Tab belongs to the terminal while it has focus, so Escape is the
         unmodified key that leaves the widget: it hands focus to the page. */
      setCycle(null)
      onEscape?.()
    } else if (e.key === 'Tab' && !e.shiftKey) {
      /* Tab belongs to the terminal: it completes, or shows what it can
         complete, and never throws the visitor out of the field mid-command.
         Shift+Tab is never taken, so that is the way out. */
      e.preventDefault()
      const next = onTab(value, cycle)
      setCycle(next.list.length > 1 ? next : null)
      if (next.value !== value) setValueEnd(next.value)
    }
  }

  const before = value.slice(0, sel)
  const at = value[sel] ?? '\u00a0'
  const after = value.slice(sel + 1)

  return (
    <form
      className={className}
      onSubmit={(e) => {
        e.preventDefault()
        commit()
      }}
    >
      <span className="ps1">❯</span>
      <div className="init__term" onClick={() => inputRef.current?.focus()}>
        <span className="init__term-echo" aria-hidden="true">
          <span className="init__term-text">
            {before}
            <span className={`init__term-cursor ${!focused ? 'init__term-cursor--blur' : ''}`}>{at}</span>
            {after}
          </span>
          {value === '' && !focused ? <span className="init__term-ph">{placeholder}</span> : null}
        </span>
        <input
          ref={inputRef}
          className="init__term-input"
          value={value}
          onChange={(e) => {
            setValue(e.target.value)
            setSel(e.target.selectionStart ?? e.target.value.length)
            setCycle(null)
          }}
          onKeyDown={onKeyDown}
          onKeyUp={syncSel}
          onClick={syncSel}
          onSelect={syncSel}
          onFocus={() => {
            setFocused(true)
            syncSel()
          }}
          onBlur={() => {
            setFocused(false)
            setCycle(null)
          }}
          placeholder={placeholder}
          aria-label="Command"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
        />
      </div>
      {cycle && cycle.list.length > 1 ? (
        <>
          <div className="completions" aria-hidden="true">
            {cycle.list.map((candidate, index) => (
              <span key={candidate} className={index === cycle.index ? 'completions__item is-on' : 'completions__item'}>
                {candidate.split(' ').pop()}
              </span>
            ))}
          </div>
          {/* The list is a picture of the answer; this is the answer itself, for
              anyone who is not looking at it. */}
          <span className="sr-only" role="status">
            {cycle.index >= 0 ? cycle.list[cycle.index].split(' ').pop() : cycle.list.join(', ')}
          </span>
        </>
      ) : null}
    </form>
  )
}

/* ---------------------------------------------------------------- init */

const INIT_FLAG = 'jfrader:init-seen'

function readInitSeen(): boolean {
  if (typeof window === 'undefined') return true
  try {
    return window.localStorage.getItem(INIT_FLAG) === '1'
  } catch {
    return true
  }
}

function writeInitSeen(): void {
  try {
    window.localStorage.setItem(INIT_FLAG, '1')
  } catch {
    // storage unavailable: the init screen will simply show again next visit
  }
}

/* The fastfetch block beside the penguin. Only facts already in src/content.ts,
   and the values that have somewhere to go are links, as they are in the rail's
   own output. */
const INIT_FACTS: [string, ReactNode][] = [
  ['role', 'full-stack engineer'],
  ['stack', 'TypeScript · React · Node.js · PostgreSQL · Rust · Linux'],
  ['domains', 'fintech · payments · ecommerce · media · marketplaces · games'],
  ['bitcoin', 'Bitcoin and Lightning payments'],
  ['games', GAMES.map((g, i) => (
    <Fragment key={g.name}>
      {i > 0 && ' · '}
      <XLink href={g.url}>{g.name}</XLink>
    </Fragment>
  ))],
  ['github', <XLink href={GITHUB_URL}>{GITHUB_HANDLE}</XLink>],
]

/* Fran's penguin, converted from fran-sencillo.png by downsampling onto the
   init palette. b = dark head, w = light belly/eye patches, g = grey body,
   o = orange beak and feet. The blink variant closes the eyes by swapping the
   two pupil cells for white. */
const PENGUIN = [
  '            bbbbbbbbbb            ',
  '         bbbbbbbbbbbbbbbb         ',
  '        bbbbbbbbbbbbbbbbbbbb      ',
  '       bbbbbbbbbbbbbbbbbbbbbb     ',
  '      bbwwwwwwwwbbbbwwwwwwwwbb    ',
  '      bwwwwwwwwwwbbbbwwwwwwwwwwb  ',
  '      bwwwwbbwwwwbbbbwwwwbbwwwwb  ',
  '      bwwwwbbwwwwbbbbwwwwbbwwwwb  ',
  '      bbwwwwwwwwbbbbbbwwwwwwwwbb  ',
  '       bwwwwwwwwbboobbwwwwwwwwb   ',
  '       bbbbbbbbbbbbbbbbbbbbbb     ',
  '        bbbbwwwwwwwwwwwwbbbb      ',
  '        ggggwwwwwwwwwwwwgggg      ',
  '        ggggwwwwwwwwwwwwgggg      ',
  '       gggggwwwwwwwwwwwwggggg     ',
  '      ggggggwwwwwwwwwwwwgggggg    ',
  '     gggggggwwwwwwwwwwwwggggggg   ',
  '     gggggggwwwwwwwwwwwwggggggg   ',
  '    ggggggggwwwwwwwwwwwwgggggggg  ',
  '   gggggggggwwwwwwwwwwwwggggggggg ',
  '  ggggggggggwwwwwwwwwwwwggggggggg ',
  ' gggggg  gggwwwwwwwwwwwwggg  ggggg',
  '        gggwwwwwwwwwwwwggg        ',
  '         gggggggggggggggg         ',
  '    gggoooggggggggggggggoooggg    ',
]

const PENGUIN_BLINK = [
  '            bbbbbbbbbb            ',
  '         bbbbbbbbbbbbbbbb         ',
  '        bbbbbbbbbbbbbbbbbbbb      ',
  '       bbbbbbbbbbbbbbbbbbbbbb     ',
  '      bbwwwwwwwwbbbbwwwwwwwwbb    ',
  '      bwwwwwwwwwwbbbbwwwwwwwwwwb  ',
  '      bwwwwwwwwwwbbbbwwwwwwwwwwb  ',
  '      bwwwwwwwwwwbbbbwwwwwwwwwwb  ',
  '      bbwwwwwwwwbbbbbbwwwwwwwwbb  ',
  '       bwwwwwwwwbboobbwwwwwwwwb   ',
  '       bbbbbbbbbbbbbbbbbbbbbb     ',
  '        bbbbwwwwwwwwwwwwbbbb      ',
  '        ggggwwwwwwwwwwwwgggg      ',
  '        ggggwwwwwwwwwwwwgggg      ',
  '       gggggwwwwwwwwwwwwggggg     ',
  '      ggggggwwwwwwwwwwwwgggggg    ',
  '     gggggggwwwwwwwwwwwwggggggg   ',
  '     gggggggwwwwwwwwwwwwggggggg   ',
  '    ggggggggwwwwwwwwwwwwgggggggg  ',
  '   gggggggggwwwwwwwwwwwwggggggggg ',
  '  ggggggggggwwwwwwwwwwwwggggggggg ',
  ' gggggg  gggwwwwwwwwwwwwggg  ggggg',
  '        gggwwwwwwwwwwwwggg        ',
  '         gggggggggggggggg         ',
  '    gggoooggggggggggggggoooggg    ',
]

const PENGUIN_COLOR: Record<string, string> = {
  b: 'a-b',
  w: 'a-w',
  g: 'a-g',
  o: 'a-o',
}

/* The reveal order: ./init types, then the art prints a row at a time, then the
   facts land a line each, then the menu fills in. Every step is a CSS delay; the
   stage lengths below are derived from the same numbers, so the JS that decides
   when a stage mounts and the CSS that staggers inside it cannot drift apart. */
const ART_ROWS = PENGUIN.length
const FACT_LINES = INIT_FACTS.length + 1
const ART_STEP = 15
const FACT_STEP = 90
const MENU_STEP = 45
const ART_MS = ART_ROWS * ART_STEP
const FACTS_MS = FACT_LINES * FACT_STEP

/** Zero when the intro was skipped, so everything lands at once. */
function delayStyle(skipped: boolean, ms: number) {
  return { animationDelay: skipped ? '0ms' : `${ms}ms` }
}

function PenguinArt({ blink, step = ART_STEP }: { blink?: boolean; step?: number }) {
  const art = blink ? PENGUIN_BLINK : PENGUIN
  return (
    <>
      {art.map((row, i) => (
        <div
          key={i}
          className="init__art-row"
          style={{
            animationDelay: `${i * step}ms`,
            gridTemplateColumns: `repeat(${row.length}, 1fr)`,
            width: `calc(${row.length} * var(--init-cell-width))`,
          }}
        >
          {row.split('').map((ch, j) =>
            ch === ' ' ? null : (
              <span key={j} className={`a ${PENGUIN_COLOR[ch]}`} style={{ gridColumn: j + 1 }} />
            ),
          )}
        </div>
      ))}
    </>
  )
}

/** A row as plain text, for the places that have a single line to say it in. */
const rowText = (row: Row): string => {
  if (row.kind === 'text' || row.kind === 'error') return row.text
  if (row.kind === 'entry') return row.note ? `${row.name} ${row.note}` : row.name
  return ''
}

/* What landing on a route prints, and what the rail says it ran. One line, so
   the command shown and the output under it cannot drift. */
const LANDING = 'cat README.md'

/* The intro is the front door, so it stands at home whatever URL the visitor
   arrived on: the prompt says `~` and the field resolves from the root. */
const INTRO_ROUTE = '/'
const INTRO_CWD = pathFor(dirFor(INTRO_ROUTE))

function RootComponent() {
  const isDev = import.meta.env.DEV
  const { pathname } = useLocation()
  /* nginx serves /about as /about/, so the browser's pathname carries a
     trailing slash on every deep link. Comparing it directly to a route would
     miss and fall back to the home narration, leaving /about, /projects and the
     rest narrating whoami in both the prerendered HTML and the browser. */
  const route = pathname.replace(/\/+$/, '') || '/'
  const navigate = useNavigate()

  const [lines, setLines] = useState<Line[]>([])
  const [history, setHistory] = useState<string[]>([])
  const rememberCommand = useCallback((raw: string) => {
    setHistory((h) => (h[h.length - 1] === raw ? h : [...h, raw]))
  }, [])

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const logRef = useRef<HTMLDivElement | null>(null)
  const progressRef = useRef<HTMLElement | null>(null)
  const observer = useRef<IntersectionObserver | null>(null)
  const nextId = useRef(0)
  const initInputRef = useRef<HTMLInputElement | null>(null)
  const toggleRef = useRef<HTMLButtonElement | null>(null)
  /* whether the phone sheet was open when the visitor moved, so focus can go
     back to the button that opened it instead of being dropped on the body */
  const sheetWasOpen = useRef(false)
  /* A programmatic focus on a touch device throws the on-screen keyboard over
     the intro, so there the field waits for a tap instead. jsdom has no
     matchMedia, hence the guard. */
  const [coarse] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(pointer: coarse)').matches,
  )

  /* motion is on by default; ?motion=0 is the escape hatch. It is read once up
     front so the init screen can decide whether to show at all. */
  const [motionOff] = useState(
    () => typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('motion') === '0',
  )
  /* The system setting asks for less motion. It is a different thing from
     ?motion=0: that one skips the intro, this one plays it without the moving
     parts and renders typed output at once. */
  const [reduceMotion] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )
  const still = motionOff || reduceMotion
  useEffect(() => {
    document.documentElement.classList.toggle('no-motion', still)
  }, [still])

  /* first visit gating: the full-viewport terminal only runs before the flag
     is set, and never under ?motion=0 which lands on the end state directly. */
  const [initDone, setInitDone] = useState(readInitSeen)
  /* 0 = typing ./init, 1 = penguin + facts, 2 = menu, 3 = live input */
  /* 0 = typing ./init, 1 = hero, 2 = menu, 3 = live input. The stagger inside a
     stage is CSS, so this advances three times rather than once per item: the
     old per-item counter re-rendered the whole shell 39 times and its wall-clock
     duration stretched under CPU load, because it was a chain of JS timers. */
  const [initStage, setInitStage] = useState(0)
  /* with motion still there is nothing to stagger, so the reveal lands at once */
  const [skipped, setSkipped] = useState(still)
  const [blinking, setBlinking] = useState(false)
  const [poked, setPoked] = useState(false)
  const [initError, setInitError] = useState('')
  const [termOpen, setTermOpen] = useState(false)
  const showInit = !initDone && !motionOff
  const docHidden = showInit || termOpen

  /* index.html hid the prerendered layout for a first visit, before it could
     paint. The shell has rendered now, so let it through, and do it before the
     browser paints this frame so nothing flashes. */
  useLayoutEffect(() => {
    document.documentElement.removeAttribute('data-init-pending')
  }, [])

  /* The working directory is the route's node, not state: every way of moving
     (a link, a button, `cd`, the back button) lands the prompt in one place. */
  const dir = dirFor(route)
  const cwd = pathFor(dir)

  /* The penguin is a pet, not a link. Clicking it blinks and hops, and never
     goes anywhere. */
  const pokePenguin = useCallback(() => {
    if (still) return
    setBlinking(true)
    setPoked(true)
    window.setTimeout(() => setPoked(false), 340)
    window.setTimeout(() => setBlinking(false), 160)
  }, [still])

  /* typing ./init anywhere replays the intro from the top, and from the root
     page: the intro introduces the root, so INIT takes you back to it first */
  const restartInit = useCallback(() => {
    if (pathname !== '/') navigate({ to: '/' })
    if (scrollRef.current) scrollRef.current.scrollTop = 0
    setTermOpen(false)
    setInitStage(0)
    setSkipped(false)
    setInitError('')
    setInitDone(false)
  }, [navigate, pathname])

  const enterDestination = useCallback(
    (to: To) => {
      writeInitSeen()
      setInitDone(true)
      navigate({ to })
      /* the rail's field takes focus as it mounts (see autoFocus below); on a
         touch device it waits for a tap so the keyboard does not cover the page */
    },
    [navigate],
  )

  /* Runs one shell line. The effect decides what happens: a `cd` moves, a `cat`
     prints, `clear` empties the log. Nothing is matched as a string here. */
  const runCommand = useCallback(
    (raw: string) => {
      const command = raw.trim()
      if (!command) return
      const effect = run(route, command)

      if (effect.kind === 'clear') {
        setLines([])
        return
      }
      if (effect.kind === 'init') {
        restartInit()
        return
      }

      /* The echo is the line as typed, standing in the directory it ran in. */
      const echo: Line = { id: nextId.current++, node: <Prompt cmd={command} cwd={cwd} /> }
      if (effect.kind === 'navigate') {
        setLines((prev) => [...prev, echo])
        navigate({ to: effect.to })
        return
      }
      const rows: Line[] = effect.rows.map((row) => ({ id: nextId.current++, row }))
      setLines((prev) => [...prev, echo, ...rows])
    },
    [route, cwd, navigate, restartInit],
  )

  /* Landing on a route says what that directory holds, the same way every time:
     one command, one README. The run key restarts the typing on the next route
     even though the command is identical. */
  const typing = useTypewriter(
    showInit ? './init' : LANDING,
    () => {
      if (showInit) {
        setInitStage(1)
        return
      }
      setLines((prev) => [
        ...prev,
        { id: nextId.current++, node: <Prompt cmd={LANDING} cwd={cwd} /> },
        ...dir.doc.map((row): Line => ({ id: nextId.current++, row })),
      ])
    },
    showInit ? 92 : 26,
    showInit ? 'init' : `route ${route}`,
    still,
  )

  /* Nothing waits on a timer chain any more: the whole intro mounts at once and
     the ordering is CSS delays, so the sequence is the same length whatever the
     CPU is doing and the field is usable from the first moment. */
  useEffect(() => {
    if (!showInit || initStage >= 1) return
    const skip = () => {
      setSkipped(true)
      setInitStage(1)
    }
    window.addEventListener('keydown', skip)
    window.addEventListener('pointerdown', skip)
    return () => {
      window.removeEventListener('keydown', skip)
      window.removeEventListener('pointerdown', skip)
    }
  }, [showInit, initStage])

  /* The field is ready to type into the moment the reveal finishes, and any
     click on the intro that is not on a destination puts it back.
     It also takes focus when the intro finishes and the rail mounts. */
  useEffect(() => {
    if (coarse) return
    if (showInit && initStage >= 1) {
      initInputRef.current?.focus()
    } else if (!showInit) {
      initInputRef.current?.focus()
    }
  }, [showInit, initStage, coarse])

  /* The penguin blinks at irregular intervals once the hero is visible. */
  useEffect(() => {
    if (!showInit || initStage < 1 || still) return
    let alive = true
    let closeTimer = 0
    let openTimer = 0
    const schedule = () => {
      if (!alive) return
      openTimer = window.setTimeout(() => {
        if (!alive) return
        setBlinking(true)
        closeTimer = window.setTimeout(() => {
          if (!alive) return
          setBlinking(false)
          schedule()
        }, 130 + Math.random() * 60)
      }, 2600 + Math.random() * 2400)
    }
    schedule()
    return () => {
      alive = false
      window.clearTimeout(openTimer)
      window.clearTimeout(closeTimer)
    }
  }, [showInit, initStage, still])

  /* keep the log pinned to the bottom */
  useEffect(() => {
    const el = logRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [lines, typing])

  /* Reveal wiring. Lazy routes mount their content after the route changes, so
     this cannot be a one-shot pass on the next frame: a MutationObserver keeps
     wiring whatever appears, and each element is observed only once. */
  const ensureObserver = useCallback(() => {
    if (observer.current) return observer.current
    const root = scrollRef.current
    if (!root) return null
    observer.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          entry.target.classList.add('rv--in')
          observer.current?.unobserve(entry.target)
        })
      },
      { root, rootMargin: '0px 0px -12% 0px', threshold: 0.05 },
    )
    return observer.current
  }, [])

  const wireReveals = useCallback(() => {
    const root = scrollRef.current
    const io = ensureObserver()
    if (!root || !io) return
    root.querySelectorAll('.rv:not(.rv--in)').forEach((el, index) => {
      const node = el as HTMLElement & { dataset: DOMStringMap }
      if (node.dataset.wired === '1') return
      node.dataset.wired = '1'
      node.style.setProperty('--i', String(index))
      io.observe(node)
    })
  }, [ensureObserver])

  useLayoutEffect(() => {
    const root = scrollRef.current
    if (root) root.scrollTop = 0
  }, [pathname])

  /* On a phone the terminal is a sheet over the page, so reaching another page
     closes it rather than leaving the page covered. On a desktop the rail is
     always visible and this changes nothing. When it was open, focus goes back
     to the button that opened it: the sheet it was inside is gone. */
  useEffect(() => {
    if (sheetWasOpen.current) {
      sheetWasOpen.current = false
      toggleRef.current?.focus()
    }
    setTermOpen(false)
  }, [pathname])

  const toggleSheet = useCallback(() => {
    const next = !termOpen
    sheetWasOpen.current = next
    setTermOpen(next)
    /* into the field when it opens, back to the button when it closes */
    if (next) requestAnimationFrame(() => initInputRef.current?.focus())
    else toggleRef.current?.focus()
  }, [termOpen])

  useEffect(() => {
    const root = scrollRef.current
    if (!root) return
    const frame = requestAnimationFrame(wireReveals)
    const mutations = new MutationObserver(() => wireReveals())
    mutations.observe(root, { childList: true, subtree: true })
    return () => {
      cancelAnimationFrame(frame)
      mutations.disconnect()
    }
  }, [pathname, wireReveals])

  /* release both observers when the shell goes away */
  useEffect(
    () => () => {
      observer.current?.disconnect()
      observer.current = null
    },
    [],
  )

  /* the progress bar is written straight to the DOM: no re-render per scroll frame */
  const onScroll = useCallback(() => {
    const el = scrollRef.current
    const bar = progressRef.current
    if (!el) return
    const max = el.scrollHeight - el.clientHeight
    if (bar) bar.style.width = `${max > 0 ? Math.min(100, (el.scrollTop / max) * 100) : 100}%`
    // reaching the bottom reveals whatever the observer's band missed
    if (max > 0 && el.scrollTop >= max - 4) {
      el.querySelectorAll('.rv:not(.rv--in)').forEach((n) => n.classList.add('rv--in'))
    }
  }, [])

  /* The intro's field runs the same shell. A destination moves; anything else
     prints under the field, because the intro has no log to scroll. */
  const onInitSubmit = (raw: string) => {
    const command = raw.trim()
    if (!command) return
    const effect = run(INTRO_ROUTE, command)

    switch (effect.kind) {
      case 'navigate':
        enterDestination(effect.to)
        return
      case 'init':
        restartInit()
        return
      case 'clear':
        setInitError('')
        return
      case 'print': {
        const text = effect.rows.map(rowText).filter(Boolean).join(' · ')
        setInitError(text.length > 200 ? `${text.slice(0, 199)}…` : text)
      }
    }
  }

  const shellClass = [
    'shell',
    showInit ? 'shell--init' : '',
    termOpen ? 'shell--open' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      <HeadContent />
      <div className={shellClass}>
        {/* First stop for a keyboard: past the terminal, into the page. */}
        <a className="skiplink" href="#doc">
          skip to content
        </a>
        {/* ==================== rail ==================== */}
        <aside className="rail" aria-label="Terminal">
          {showInit ? (
            <div
              className="init"
              onClick={(e) => {
                if ((e.target as HTMLElement).closest('button, a')) return
                initInputRef.current?.focus()
              }}
            >
              <div className="init__head" aria-hidden="true">
                <span className="rail__host">[user@jfrader {INTRO_CWD}]</span>
              </div>
              <div className="init__inner">
                <div className="init__line">
                  <Prompt cmd={typedOrInit(typing, initStage >= 1)} cwd={INTRO_CWD} />
                  {initStage === 0 && <span className="caret caret--inline" />}
                </div>

                {initStage >= 1 && (
                  <div className="init__hero">
                    <div
                      className={`init__art${poked ? ' init__art--poke' : ''}`}
                      aria-hidden="true"
                      onClick={pokePenguin}
                    >
                      <PenguinArt blink={blinking} step={skipped ? 0 : ART_STEP} />
                    </div>
                    <div className="init__meta">
                      <div className="init__who" style={delayStyle(skipped, ART_MS)}>user@jfrader</div>
                      <div className="init__sep" aria-hidden="true" style={delayStyle(skipped, ART_MS + 40)} />
                      {INIT_FACTS.map(([k, v], i) => (
                        <div className="init__row" key={k} style={delayStyle(skipped, ART_MS + 80 + i * FACT_STEP)}>
                          <span className="init__key">{k}</span>
                          <span className="init__val">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {initStage >= 1 && (
                  <nav className="init__menu" aria-label="Destinations">
                    {ROUTES.map((r, i) => (
                      <DestinationButton
                        key={r.label}
                        to={r.to}
                        className="init__btn"
                        style={delayStyle(skipped, ART_MS + FACTS_MS + i * MENU_STEP)}
                        onClick={() => enterDestination(r.to)}
                      >
                        <span className="init__btn-label">{r.label}</span>
                        <span className="init__btn-hint">{r.cmd}</span>
                      </DestinationButton>
                    ))}
                  </nav>
                )}

                {/* the field is present and focusable from the start, so the
                    intro never holds up typing; a keystroke also ends the reveal */}
                {initStage >= 1 && (
                  <>
                    <TerminalInput
                      onSubmit={onInitSubmit}
                      placeholder="type a command"
                      fieldRef={initInputRef}
                      history={history}
                      onRun={rememberCommand}
                      onTab={(draft, from) => tab(INTRO_ROUTE, draft, from)}
                    />
                    {initError && <div className="init__error muted">{initError}</div>}
                  </>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="rail__bar">
                <span className="rail__host">[user@jfrader {cwd}]</span>
                <span className="grow" />
                <button
                  ref={toggleRef}
                  type="button"
                  className={termOpen ? 'term-toggle term-toggle--open' : 'term-toggle'}
                  onClick={toggleSheet}
                  aria-expanded={termOpen}
                  aria-controls="rail-body"
                  aria-label={termOpen ? 'Close terminal' : 'Open terminal'}
                >
                  <span aria-hidden="true">{'>_'}</span>
                </button>
                <button
                  type="button"
                  className="skip"
                  aria-label="Replay intro"
                  onClick={() => restartInit()}
                >
                  INIT
                </button>
              </div>

              <div className="rail__body" id="rail-body">
                <div className="rail__log" ref={logRef} role="log" aria-label="Terminal output" aria-live="polite">
                  {lines.map((line) => (
                    <div key={line.id} className="ln">
                      {holdsRow(line) ? <RowView row={line.row} onRun={runCommand} /> : line.node}
                    </div>
                  ))}
                  {typing !== null && (
                    <div className="ln">
                      <Prompt cmd={typing} cwd={cwd} />
                      <span className="caret caret--inline" />
                    </div>
                  )}
                </div>

                <div className="palette">
                  <span className="palette__label">run</span>
                  {ROUTES.map((r) => (
                    <DestinationButton key={r.label} to={r.to} onClick={() => runCommand(r.cmd)}>
                      {r.label}
                    </DestinationButton>
                  ))}
                </div>

                <TerminalInput
                  className="rail__live"
                  onSubmit={runCommand}
                  placeholder="or type help"
                  fieldRef={initInputRef}
                  history={history}
                  onRun={rememberCommand}
                  onTab={(draft, from) => tab(route, draft, from)}
                  onEscape={() => {
                    if (termOpen) toggleSheet()
                    else scrollRef.current?.focus()
                  }}
                  autoFocus={!coarse}
                />
              </div>
            </>
          )}
        </aside>

        {/* ==================== document ==================== */}
        <main className="doc" inert={docHidden || undefined}>
          {/* One heading per page, naming the page the way the terminal does.
              The sections below it are h2, so the outline is readable. */}
          <h1 className="sr-only">{isDestination(route) ? dir.label : 'not found'}</h1>
          <nav className="nav">
            <span className="nav__who">fran, full-stack engineer</span>
            <span className="spacer" />
            <Link to="/">Home</Link>
            <Link to="/about">About</Link>
            <Link to="/products">Products</Link>
            <Link to="/projects">Projects</Link>
            <Link to="/tips">Tips</Link>
            <Link to="/contact">Contact</Link>
          </nav>
          <div className="progress" aria-hidden="true">
            <i ref={progressRef} />
          </div>
          {/* it scrolls, so it is focusable: otherwise a keyboard cannot move
              a long page at all */}
          <div className="doc__scroll" id="doc" ref={scrollRef} tabIndex={0} onScroll={onScroll}>
            <div className="doc__inner">
              <Outlet />
            </div>
          </div>
        </main>
      </div>

      {isDev && (
        <Suspense fallback={null}>
          <TanStackRouterDevtools position="bottom-right" />
          <ReactQueryDevtools initialIsOpen={false} position="bottom" />
        </Suspense>
      )}
    </>
  )
}

/** The typed `./init` while it is being entered, then the full command once done. */
function typedOrInit(typing: string | null, revealed: boolean): string {
  if (revealed) return './init'
  return typing ?? './init'
}
